'use client';
import {
  getTotalSubcategoriesAndCategory,
  sanitizeFilters,
} from '@components/common';
import FilterCategoryLists from '@components/common/searchFilter/filter/filterCategoryList';
import { Button, OneSectionCollapsible } from '@license-admin/boldjobs';
import { deferNavigation } from '@utils/deferNavigation';
import { ClickOption } from 'helper/eventTrackingUtils/constant';
import React, {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  CloseButton,
  FilterListWrap,
  FilterText,
  FlyerBody,
  FlyerFooter,
  FlyerHeader,
  FlyerSkeletonItem,
  FlyerSkeletonLine,
  FlyerSkeletonList,
  FlyerWrapper,
  MobSortFilterWrap,
} from './styled';

type Props = {
  jobFilters: Array<JobFilters>;
  selectedFilters: { [key: string]: Array<string> };
  settoggleFlyer?: any;
  filterQuery: string;
  handeleFilterChange: (
    _filterName: string,
    _filterCategory: string,
    _name: string
  ) => string;
  handleCategoryFilter: (
    _filter: any,
    _categoryId: string,
    _categoryType: 'Parent' | 'Child'
  ) => void;
  activeJob: string;
  setActiveJob: any;
  isSecondaryAccordion?: boolean;
  toggleFlyer?: boolean;
};
type JobFilters = {
  name: string;
  categoryWithCount: CategoryWithCount;
  slug: string;
  category?: any;
};

type CategoryWithCount = {
  [key: string]: number;
};

const SORT_BY_DATE_PARAM = 'sortbyposteddate';

const FilterFlyer = (props: Props) => {
  const {
    jobFilters,
    selectedFilters,
    toggleFlyer,
    activeJob,
    settoggleFlyer,
    filterQuery,
    setActiveJob,
    handeleFilterChange,
    handleCategoryFilter,
    isSecondaryAccordion,
  } = props;
  const [activeSecName, setActiveSecName] = useState('Remote');
  const [sortByFilter, setSortByFilter] = useState(activeJob);
  const [isBodyReady, setIsBodyReady] = useState(false);
  const bodyReadyRafRef = useRef<number | null>(null);

  // Memoize expensive filter sanitization
  const sanitizedFilter = useMemo(() => {
    if (!isBodyReady || !jobFilters) return [];
    // Avoid expensive JSON.stringify if not needed
    return sanitizeFilters(selectedFilters, jobFilters);
  }, [isBodyReady, jobFilters, selectedFilters]);

  // Memoize result calculation
  const result = useMemo(
    () =>
      !isBodyReady
        ? 0
        : getTotalSubcategoriesAndCategory(
            selectedFilters.categories || [],
            jobFilters
          ),
    [isBodyReady, selectedFilters.categories, jobFilters]
  );

  useEffect(() => {
    if (bodyReadyRafRef.current !== null) {
      window.cancelAnimationFrame(bodyReadyRafRef.current);
      bodyReadyRafRef.current = null;
    }

    if (!toggleFlyer) {
      setIsBodyReady(false);
      return;
    }

    bodyReadyRafRef.current = window.requestAnimationFrame(() => {
      setIsBodyReady(true);
      bodyReadyRafRef.current = null;
    });

    return () => {
      if (bodyReadyRafRef.current !== null) {
        window.cancelAnimationFrame(bodyReadyRafRef.current);
        bodyReadyRafRef.current = null;
      }
    };
  }, [toggleFlyer]);

  // Memoize count calculation to prevent re-computation on every render
  const getFilterCount = useCallback(
    (slug: string) => {
      if (slug === 'categories' && Number(result) > 0) {
        return `(${result})`;
      }
      if (selectedFilters[slug]?.length > 0) {
        return `(${selectedFilters[slug].length})`;
      }
      return '';
    },
    [result, selectedFilters]
  );

  const removeQueryParams = useCallback(() => {
    const url = window.location.href.split('?')[0];
    deferNavigation(url);
  }, []);

  const handleToggle = useCallback((name: string) => {
    window.requestAnimationFrame(() => {
      setActiveSecName((prev) => (prev === name ? '' : name));
    });
  }, []);

  const handleCloseClick = useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault();
      // Defer the heavy close/unmount work to the next frame and mark it as a
      // non-urgent transition so the click commit paints first (lower presentation delay).
      requestAnimationFrame(() => {
        startTransition(() => {
          settoggleFlyer?.(false);
        });
      });
    },
    [settoggleFlyer]
  );

  const showJobs = useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault();
      const urlParams = new URLSearchParams(filterQuery);
      if (sortByFilter === ClickOption.Date) {
        if (!urlParams.get(SORT_BY_DATE_PARAM)) {
          urlParams.append(SORT_BY_DATE_PARAM, 'true');
          urlParams.delete('page');
        }
      } else if (sortByFilter === ClickOption.Relevance) {
        urlParams.delete(SORT_BY_DATE_PARAM);
        urlParams.delete('page');
      }
      deferNavigation(window.location.pathname + '?' + urlParams.toString());
    },
    [filterQuery, sortByFilter]
  );

  // Memoize sort button handlers to prevent Button re-renders
  const handleRelevanceClick = useCallback(() => {
    setActiveJob(ClickOption.Relevance);
    setSortByFilter(ClickOption.Relevance);
  }, [setActiveJob]);

  const handleDateClick = useCallback(() => {
    setActiveJob(ClickOption.Date);
    setSortByFilter(ClickOption.Date);
  }, [setActiveJob]);

  return (
    <FlyerWrapper
      toggleFlyer={toggleFlyer}
      aria-label="Filter options panel"
      aria-hidden={!toggleFlyer || undefined}
      aria-modal="true"
      role="dialog"
    >
      <FlyerHeader>
        <Button
          text="Reset"
          buttonType="transparent"
          ButtonClass="reset_btn"
          clickHandler={removeQueryParams}
          id="flyerfilter-reset"
        />
        <FilterText>Filters</FilterText>
        <CloseButton
          onClick={handleCloseClick}
          aria-label="Close filters"
          id="flyerfilter-close"
        >
          <i className="fa fa-solid fa-xmark right-icon" aria-hidden="true"></i>
        </CloseButton>
      </FlyerHeader>
      <FlyerBody aria-busy={!isBodyReady || undefined}>
        {!isBodyReady ? (
          <FlyerSkeletonList aria-hidden>
            {Array.from({ length: 4 }).map((_, index) => (
              <FlyerSkeletonItem key={`filter-skeleton-${index}`}>
                <FlyerSkeletonLine width="52%" height="1.4rem" />
                <FlyerSkeletonLine width="82%" height="1.1rem" />
              </FlyerSkeletonItem>
            ))}
          </FlyerSkeletonList>
        ) : (
          <>
            {sanitizedFilter.map((item: any, index: number) => {
              return (
                <OneSectionCollapsible
                  id={item.slug || `${index}`}
                  key={item.slug || `${index}`}
                  hasSubCategory
                  isFilterFlyer
                  headerClass="account-accordion"
                  name={item.name.trim()}
                  handleToggle={handleToggle}
                  activeSecName={activeSecName}
                  autoHeight={
                    item.name === 'Career Level' || item.name === 'Title'
                  }
                  isSecondaryAccordion={isSecondaryAccordion}
                >
                  <div>
                    {item.name} {getFilterCount(item.slug)}
                  </div>
                  <FilterListWrap>
                    <FilterCategoryLists
                      categoryVal={item.category}
                      categoryData={item.categoryWithCount}
                      changeHandler={handeleFilterChange}
                      handleCategoryFilter={handleCategoryFilter}
                      {...item}
                      selectedCategories={selectedFilters[item.slug]}
                      parentId={item.slug || `${index}`}
                    />
                  </FilterListWrap>
                </OneSectionCollapsible>
              );
            })}
            {Array.isArray(selectedFilters?.searchkeyword) &&
              !!selectedFilters?.searchkeyword.length && (
                <OneSectionCollapsible
                  hasSubCategory
                  isFilterFlyer
                  headerClass="account-accordion"
                  handleToggle={handleToggle}
                  name={'Sort Jobs by:'}
                  activeSecName={activeSecName}
                  isSecondaryAccordion={isSecondaryAccordion}
                >
                  <div>Sort Jobs by:</div>
                  <MobSortFilterWrap>
                    <Button
                      ButtonClass="sort-btn"
                      text="Relevance"
                      buttonType={
                        activeJob === 'relevance' ? 'primary' : 'secondary'
                      }
                      bdrRadius="0.4rem 0 0 0.4rem"
                      clickHandler={handleRelevanceClick}
                      buttonSize="sm"
                      fullLength
                    />
                    <Button
                      text="Date"
                      clickHandler={handleDateClick}
                      buttonSize="sm"
                      buttonType={
                        activeJob === 'date' ? 'primary' : 'secondary'
                      }
                      bdrRadius="0 0.4rem 0.4rem 0"
                      ButtonClass="sort-btn"
                      fullLength
                    />
                  </MobSortFilterWrap>
                </OneSectionCollapsible>
              )}
          </>
        )}
      </FlyerBody>
      <FlyerFooter>
        <Button
          text="Show Jobs"
          buttonType="primary"
          fullLength
          clickHandler={showJobs}
          id="show-jobs-btn"
          ButtonClass="show-jobs-btn"
        />
      </FlyerFooter>
    </FlyerWrapper>
  );
};

export default React.memo(FilterFlyer);
